import { reportProviderWorkingLocation } from "../provider-workspace/provider-working-location";
import { createPortal } from "react-dom";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type {
  FoodItemRecord,
  PaymentMethod,
  ProviderFoodOrder,
  ProviderFoodOrderStatus,
  ProviderSession,
  ProviderUnit,
  ProviderVenue,
  StartWalkInSessionInput,
  StopProviderSessionInput,
  UnitStatus,
  UnitType,
} from "../../lib/api";
import { ApiError, providerApi } from "../../lib/api";
import {
  calculateSessionElapsedTotalAmount,
  calculateSessionPlannedTotalAmount,
  formatPricingAmount,
} from "../../lib/provider-pricing";
import { useAppPreferences } from "../../lib/preferences";
import { subscribeRealtimeEvent } from "../../lib/realtime";
import { OrderComposer } from "../provider-orders/OrderComposer";
import { NoShiftCashNotice } from "../provider-shift/TodayCards";
import { WalkInDialog } from "./WalkInDialog";
import { ChefHat, Hourglass } from "lucide-react";
import { formatEgp } from "../provider-orders/order-status";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/Dialog";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";

interface LiveUnitRecord {
  session: ProviderSession | null;
  status: UnitStatus;
  unit: ProviderUnit;
}

const UNIT_TYPE_ORDER: UnitType[] = ["console", "pc", "billiards", "pingpong", "room", "vr", "simulator", "other"];
/** Status, type and search filters appear once a floor has more units than this. */
const FILTERS_FROM_UNITS = 6;

const UNIT_STATUS_ORDER: UnitStatus[] = ["available", "occupied", "reserved", "blocked", "offline"];

function formatClock(value: string, timeZone: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    timeZone,
  }).format(new Date(value));
}

function formatLocalTime(nowMs: number, timeZone: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).format(new Date(nowMs));
}

function formatDuration(minutes: number, t: (key: string, values?: Record<string, string | number | null | undefined>) => string) {
  if (minutes < 60) {
    return t("{minutes} min", { minutes });
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? t("{hours}h", { hours })
    : t("{hours}h {minutes}m", { hours, minutes: remainingMinutes });
}

function formatCountdown(seconds: number) {
  const absoluteSeconds = Math.max(0, seconds);
  const hours = Math.floor(absoluteSeconds / 3600);
  const minutes = Math.floor((absoluteSeconds % 3600) / 60);
  const remainingSeconds = absoluteSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function getElapsedMinutes(session: ProviderSession, nowMs: number) {
  if (session.ended_at) {
    return session.duration_minutes;
  }

  const startedAtMs = new Date(session.started_at).getTime();
  return Math.max(session.duration_minutes, Math.floor((nowMs - startedAtMs) / 60000));
}

function getOvertimeMinutes(session: ProviderSession, nowMs: number) {
  if (!session.planned_end_at) {
    return 0;
  }

  if (session.ended_at) {
    return session.overtime_minutes;
  }

  const plannedEndMs = new Date(session.planned_end_at).getTime();
  return Math.max(0, Math.floor((nowMs - plannedEndMs) / 60000));
}

function getRemainingSeconds(session: ProviderSession, nowMs: number) {
  if (!session.planned_end_at) {
    return null;
  }

  return Math.floor((new Date(session.planned_end_at).getTime() - nowMs) / 1000);
}

function getSessionProgress(session: ProviderSession, nowMs: number) {
  if (!session.planned_end_at) {
    return null;
  }

  const startedAtMs = new Date(session.started_at).getTime();
  const plannedEndAtMs = new Date(session.planned_end_at).getTime();
  const totalMs = plannedEndAtMs - startedAtMs;
  if (totalMs <= 0) {
    return 100;
  }

  return Math.min(100, Math.max(0, ((nowMs - startedAtMs) / totalMs) * 100));
}

function formatFoodOrderStatus(status: ProviderFoodOrderStatus) {
  switch (status) {
    case "preparing":
      return "Preparing";
    case "ready":
      return "Ready";
    case "delivered":
      return "Delivered";
    case "cancelled":
      return "Cancelled";
    default:
      return "Pending";
  }
}

function getFoodOrderTone(status: ProviderFoodOrderStatus) {
  switch (status) {
    case "pending":
      return "warning";
    case "preparing":
      return "info";
    case "ready":
      return "info";
    case "delivered":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "info";
  }
}

function getFoodOrderActions(status: ProviderFoodOrderStatus) {
  switch (status) {
    case "pending":
      return [
        { label: "Start preparing", nextStatus: "preparing" as const },
        { label: "Cancel order", nextStatus: "cancelled" as const },
      ];
    case "preparing":
      return [
        { label: "Mark ready", nextStatus: "ready" as const },
        { label: "Cancel order", nextStatus: "cancelled" as const },
      ];
    case "ready":
      return [
        { label: "Mark served", nextStatus: "delivered" as const },
        { label: "Cancel order", nextStatus: "cancelled" as const },
      ];
    default:
      return [];
  }
}

function isActionableFoodOrderStatus(status: ProviderFoodOrderStatus) {
  return status === "pending" || status === "preparing" || status === "ready";
}

function groupFoodOrders(orders: ProviderFoodOrder[]) {
  return orders.reduce<Record<string, ProviderFoodOrder[]>>((grouped, order) => {
    // Counter sales belong to no table.
    if (!order.session_id) return grouped;
    const ordersForSession = grouped[order.session_id] ?? [];
    if (!ordersForSession.some((existing) => existing.id === order.id)) {
      ordersForSession.push(order);
    }
    grouped[order.session_id] = ordersForSession;
    return grouped;
  }, {});
}

function mergeFoodOrder(
  grouped: Record<string, ProviderFoodOrder[]>,
  nextOrder: ProviderFoodOrder,
) {
  if (!nextOrder.session_id) return grouped;
  const next = { ...grouped };
  const current = next[nextOrder.session_id] ?? [];
  next[nextOrder.session_id] = [
    nextOrder,
    ...current.filter((order) => order.id !== nextOrder.id),
  ];
  return next;
}

function getUnitTypeLabel(type: UnitType) {
  switch (type) {
    case "console":
      return "Console";
    case "pc":
      return "PC";
    case "vr":
      return "VR";
    case "simulator":
      return "Simulator";
    case "billiards":
      return "Billiards";
    case "pingpong":
      return "Ping pong";
    case "room":
      return "Room";
    default:
      return "Other";
  }
}

function getUnitStatusLabel(status: UnitStatus | "all") {
  switch (status) {
    case "available":
      return "Available";
    case "occupied":
      return "Occupied";
    case "reserved":
      return "Reserved";
    case "blocked":
      return "Blocked";
    case "offline":
      return "Offline";
    default:
      return "All";
  }
}

function getUnitStatusTone(status: UnitStatus) {
  switch (status) {
    case "available":
      return "success";
    case "occupied":
      return "info";
    case "reserved":
      return "warning";
    case "blocked":
      return "danger";
    default:
      return undefined;
  }
}

const ENDING_SOON_THRESHOLD_SECONDS = 15 * 60;

type SessionEscalation = "overtime" | "ending" | "running" | null;

/** KDS-style time escalation: running -> ending soon (<15 min) -> overtime. */
function getSessionEscalation(session: ProviderSession, nowMs: number): SessionEscalation {
  if (!session.planned_end_at) {
    return null;
  }
  const remainingSeconds = getRemainingSeconds(session, nowMs);
  if (remainingSeconds === null) {
    return null;
  }
  if (remainingSeconds < 0) {
    return "overtime";
  }
  if (remainingSeconds < ENDING_SOON_THRESHOLD_SECONDS) {
    return "ending";
  }
  return "running";
}

/** Lower rank = closer to the top of the live grid. Overtime always leads. */
function getUnitUrgencyRank(record: LiveUnitRecord, nowMs: number) {
  if (record.session) {
    const escalation = getSessionEscalation(record.session, nowMs);
    if (escalation === "overtime") return 0;
    if (escalation === "ending") return 1;
    return 2;
  }
  switch (record.status) {
    case "reserved":
      return 3;
    case "available":
      return 4;
    case "blocked":
      return 5;
    default:
      return 6;
  }
}

export function ProviderSessionsCard({
  creatingWalkIn,
  error,
  extendingSessionId,
  isLoading,
  notice,
  onCheckoutSession,
  onExtendSession,
  onStartWalkInSession,
  onStopSession,
  recentSessions,
  selectedVenue,
  sessions,
  stoppingSessionId,
}: {
  creatingWalkIn: boolean;
  error: string | null;
  extendingSessionId: string | null;
  isLoading: boolean;
  notice: string | null;
  onCheckoutSession?: (sessionId: string) => void;
  onExtendSession: (sessionId: string, additionalMinutes: number) => Promise<void>;
  /** Resolves false when the walk-in could not be started, so its form stays open. */
  onStartWalkInSession: (payload: StartWalkInSessionInput) => Promise<boolean | void>;
  onStopSession: (sessionId: string, payload: StopProviderSessionInput) => Promise<unknown>;
  recentSessions: ProviderSession[];
  selectedVenue: ProviderVenue | null;
  sessions: ProviderSession[];
  stoppingSessionId: string | null;
}) {
  const { resolvedLanguage, t } = useAppPreferences();
  const { notificationTarget, isOnline, navigateToSection } = useProviderWorkspace();
  const consumedNotificationTargetRef = useRef<typeof notificationTarget>(null);
  const locale = resolvedLanguage === "ar" ? "ar-EG" : "en-US";
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [unitStatusFilter, setUnitStatusFilter] = useState<UnitStatus | "all">("all");
  const [unitTypeFilter, setUnitTypeFilter] = useState<UnitType | "all">("all");
  const [unitSearch, setUnitSearch] = useState("");
  const [endingSessionId, setEndingSessionId] = useState<string | null>(null);
  const [expandedFoodSessionId, setExpandedFoodSessionId] = useState<string | null>(null);
  const [settlementsOpen, setSettlementsOpen] = useState(false);
  const [walkInUnitId, setWalkInUnitId] = useState<string | null>(null);
  const [stopForms, setStopForms] = useState<Record<string, { note: string; payment_collected: boolean; payment_method: PaymentMethod; payment_note: string }>>({});
  const [foodMenuItems, setFoodMenuItems] = useState<FoodItemRecord[]>([]);
  const [foodOrdersBySession, setFoodOrdersBySession] = useState<Record<string, ProviderFoodOrder[]>>({});
  const [foodOrdersLoading, setFoodOrdersLoading] = useState(false);
  const [foodOrderError, setFoodOrderError] = useState<string | null>(null);
  const [foodOrderNotice, setFoodOrderNotice] = useState<string | null>(null);
  const [updatingFoodOrderId, setUpdatingFoodOrderId] = useState<string | null>(null);
  const foodOrderRefreshRef = useRef(0);
  const foodOrderVenueGenerationRef = useRef(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const focusedNotificationKeyRef = useRef<string | null>(null);
  const [notificationFocus, setNotificationFocus] = useState<{ sessionId: string; orderId: string | null } | null>(null);
  const foodModalTitleId = useId();
  const foodModalRef = useRef<HTMLDivElement>(null);
  const previousFoodFocusRef = useRef<HTMLElement | null>(null);

  // Historical orders remain reachable even after leaving the triage queue.
  const foodModalSession = expandedFoodSessionId
    ? [...sessions, ...recentSessions].find((session) => session.id === expandedFoodSessionId) ?? null
    : null;
  const foodModalOpen = foodModalSession !== null;
  useEffect(() => {
    if (!selectedVenue) return;
    const target = notificationTarget && "sessionId" in notificationTarget && notificationTarget.venueId === selectedVenue.id ? notificationTarget : null;
    const sessionId = foodModalSession?.id ?? target?.sessionId;
    // Only save records that the current server response actually loaded.
    if (sessionId && ![...sessions, ...recentSessions].some((session) => session.id === sessionId)) return;
    const orderId = target?.kind === "food-order" && foodModalSession?.id === target.sessionId ? target.orderId : null;
    reportProviderWorkingLocation({ destination: "floor", context: orderId
      ? { kind: "food-order", venue_id: selectedVenue.id, record_id: orderId }
      : sessionId ? { kind: "session", venue_id: selectedVenue.id, record_id: sessionId }
      : { kind: "venue", venue_id: selectedVenue.id } });
  }, [selectedVenue?.id, foodModalSession?.id, notificationTarget, sessions, recentSessions]);


  const foodVenueId = selectedVenue?.id;
  const foodVenueIdRef = useRef(foodVenueId);
  foodVenueIdRef.current = foodVenueId;

  const loadFoodOrders = useCallback(async (venueId: string) => {
    const refreshId = ++foodOrderRefreshRef.current;
    setFoodOrdersLoading(true);
    try {
      const response = await providerApi.listFoodOrders({ venueId });
      if (
        refreshId !== foodOrderRefreshRef.current ||
        foodVenueIdRef.current !== venueId
      ) return;
      setFoodOrdersBySession(groupFoodOrders(response.orders));
      setFoodOrderError(null);
    } catch (requestError) {
      if (refreshId === foodOrderRefreshRef.current && foodVenueIdRef.current === venueId) {
        setFoodOrderError(requestError instanceof ApiError ? requestError.detail : t("Could not load food ordering data."));
      }
    } finally {
      if (refreshId === foodOrderRefreshRef.current) setFoodOrdersLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    foodOrderVenueGenerationRef.current += 1;
    setFoodMenuItems([]);
    setFoodOrdersBySession({});
    setFoodOrderError(null);
    setFoodOrderNotice(null);

    foodOrderRefreshRef.current += 1;
    setFoodOrdersLoading(Boolean(foodVenueId));
    setUpdatingFoodOrderId(null);
  }, [foodVenueId]);

  useEffect(() => {
    if (foodVenueId) void loadFoodOrders(foodVenueId);
  }, [loadFoodOrders, foodVenueId]);

  const reloadFoodMenu = useCallback(async (venueId: string) => {
    try {
      const foodMenu = await providerApi.listFood(venueId);
      if (foodVenueIdRef.current === venueId) setFoodMenuItems(foodMenu.items);
    } catch (requestError: unknown) {
      if (foodVenueIdRef.current === venueId) setFoodOrderError(requestError instanceof ApiError ? requestError.detail : t("Could not load food ordering data."));
    }
  }, [t]);

  useEffect(() => {
    if (foodVenueId) void reloadFoodMenu(foodVenueId);
  }, [foodVenueId, reloadFoodMenu]);

  // Food-order modal: scroll lock, initial focus, Esc to close, Tab trap —
  // same dialog behavior as DayModal.
  useEffect(() => {
    if (!foodModalOpen) {
      return;
    }
    previousFoodFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = requestAnimationFrame(() => {
      const dialog = foodModalRef.current;
      if (dialog && !dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    });
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setExpandedFoodSessionId(null);
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      const dialog = foodModalRef.current;
      if (!dialog) {
        return;
      }
      const focusable = Array.from(
        dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element): element is HTMLElement => element instanceof HTMLElement && element.tabIndex >= 0);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(timer);
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFoodFocusRef.current?.focus?.();
    };
  }, [foodModalOpen]);

  useEffect(() => subscribeRealtimeEvent((event) => {
    if (event.type !== "food_order.created" && event.type !== "food_order.updated") return;
    const payload = event.payload;
    if (!payload || typeof payload !== "object" || !selectedVenue || !("venue_id" in payload)) return;
    if (payload.venue_id !== selectedVenue.id) return;
    void loadFoodOrders(selectedVenue.id);
    if (event.type === "food_order.created") {
      // Awareness comes from the notice + triage queue + cell badge; popping
      // a modal unprompted would hijack whatever the desk is doing.
      setFoodOrderNotice(t("New food order received — ready for triage."));
    }
  }), [loadFoodOrders, selectedVenue, t]);

  const venueTimezone = selectedVenue?.timezone ?? "UTC";
  const liveUnits = useMemo<LiveUnitRecord[]>(() => {
    const sessionByUnitId = new Map(sessions.map((session) => [session.unit_id, session]));
    return (selectedVenue?.units ?? []).map((unit) => {
      const session = sessionByUnitId.get(unit.id) ?? null;
      return { session, status: session ? "occupied" : unit.status, unit };
    });
  }, [selectedVenue, sessions]);

  const unitStatusCounts = useMemo(() => {
    const counts: Record<UnitStatus, number> = { available: 0, blocked: 0, occupied: 0, offline: 0, reserved: 0 };
    liveUnits.forEach((record) => {
      counts[record.status] += 1;
    });
    return counts;
  }, [liveUnits]);

  const availableUnits = useMemo(() => liveUnits.filter((record) => record.status === "available").map((record) => record.unit), [liveUnits]);
  const actionableFoodOrders = useMemo(
    () => Object.values(foodOrdersBySession)
      .flat()
      .filter((order) => isActionableFoodOrderStatus(order.status))
      .sort((left, right) => right.created_at.localeCompare(left.created_at)),
    [foodOrdersBySession],
  );
  const actionableFoodOrderIds = useMemo(() => new Set(actionableFoodOrders.map((order) => order.id)), [actionableFoodOrders]);
  const unpaidRecentCount = recentSessions.filter((session) => !session.payment_collected).length;
  const visibleUnitTypes = useMemo(() => UNIT_TYPE_ORDER.filter((type) => liveUnits.some((record) => record.unit.type === type)), [liveUnits]);

  const filteredLiveUnits = useMemo(() => {
    const query = unitSearch.trim().toLowerCase();
    return liveUnits.filter((record) => {
      const matchesStatus = unitStatusFilter === "all" || record.status === unitStatusFilter;
      const matchesType = unitTypeFilter === "all" || record.unit.type === unitTypeFilter;
      const matchesSearch = !query || [record.unit.name, getUnitTypeLabel(record.unit.type), record.session?.customer_name ?? "", record.session?.customer_phone ?? ""]
        .some((value) => value.toLowerCase().includes(query));
      return matchesStatus && matchesType && matchesSearch;
    });
  }, [liveUnits, unitSearch, unitStatusFilter, unitTypeFilter]);

  // One continuous live grid ordered by operational urgency (overtime first,
  // dead weight last) instead of type sections — venue health reads top-down.
  const sortedLiveUnits = useMemo(
    () =>
      [...filteredLiveUnits].sort((left, right) => {
        const rankDiff = getUnitUrgencyRank(left, nowMs) - getUnitUrgencyRank(right, nowMs);
        if (rankDiff !== 0) {
          return rankDiff;
        }
        if (left.session && right.session) {
          const leftRemaining = getRemainingSeconds(left.session, nowMs) ?? Number.MAX_SAFE_INTEGER;
          const rightRemaining = getRemainingSeconds(right.session, nowMs) ?? Number.MAX_SAFE_INTEGER;
          if (leftRemaining !== rightRemaining) {
            return leftRemaining - rightRemaining;
          }
        }
        return left.unit.name.localeCompare(right.unit.name);
      }),
    [filteredLiveUnits, nowMs],
  );

  // Notification deep-link consumption: ProviderLayout routes session and
  // food-order targets to this section and selects the target venue first.
  // Once the selected venue matches, expand the owning unit group, open the
  // food panel for order targets, and mark the exact session/order for focus
  // and highlight. Consume once per navigation intent, not once per record
  // for the lifetime of the app: A → B → A and remounts must reveal A again.
  useEffect(() => {
    if (!notificationTarget) {
      consumedNotificationTargetRef.current = null;
      setNotificationFocus(null);
      return;
    }

    if (notificationTarget.kind !== "session" && notificationTarget.kind !== "food-order") {
      return;
    }

    if (!selectedVenue) {
      return;
    }

    if (notificationTarget.venueId && notificationTarget.venueId !== selectedVenue.id) {
      return;
    }

    const orderId = notificationTarget.kind === "food-order" ? notificationTarget.orderId : null;
    if (consumedNotificationTargetRef.current === notificationTarget) {
      return;
    }

    const liveRecord = liveUnits.find((record) => record.session?.id === notificationTarget.sessionId);
    if (liveRecord?.session) {
      consumedNotificationTargetRef.current = notificationTarget;
      setUnitStatusFilter("all");
      setUnitTypeFilter("all");
      setUnitSearch("");
      setExpandedFoodSessionId(orderId ? liveRecord.session.id : null);
      focusedNotificationKeyRef.current = null;
      setNotificationFocus({ sessionId: liveRecord.session.id, orderId });
      return;
    }

    // Graceful fallback: the session already left the live floor — point at
    // the recent settlements row (and the triage queue order, when still
    // actionable) instead of expanding a group that cannot contain it.
    if (recentSessions.some((session) => session.id === notificationTarget.sessionId)) {
      consumedNotificationTargetRef.current = notificationTarget;
      setExpandedFoodSessionId(orderId ? notificationTarget.sessionId : null);
      focusedNotificationKeyRef.current = null;
      setNotificationFocus({ sessionId: notificationTarget.sessionId, orderId });
    }
  }, [liveUnits, notificationTarget, recentSessions, selectedVenue]);

  // Bring the consumed target into view and move focus to the most precise
  // element available: the order record inside the session food panel, the
  // triage queue record, the live unit row, or the recent settlements row.
  // Runs once per target, with a single fallback-to-exact upgrade while the
  // food order data is still loading into the panel.
  useEffect(() => {
    if (!notificationFocus) {
      focusedNotificationKeyRef.current = null;
      return;
    }

    const root = rootRef.current;
    if (!root) {
      return;
    }

    const { orderId, sessionId } = notificationFocus;
    // A collapsed details row exists in the DOM but cannot take focus.
    if (!settlementsOpen && recentSessions.some(session => session.id === sessionId)) return;
    const focusKey = `${sessionId}:${orderId ?? ""}:${expandedFoodSessionId === sessionId ? "dialog" : "floor"}`;
    if (focusedNotificationKeyRef.current === focusKey) {
      return;
    }

    const sessionScope = root.querySelector(`[data-notification-session-id="${sessionId}"]`);
    const orderElement = orderId
      ? foodModalRef.current?.querySelector<HTMLElement>(`[data-notification-order-id="${orderId}"]`) ??
        sessionScope?.querySelector<HTMLElement>(`[data-notification-order-id="${orderId}"]`) ??
        root.querySelector<HTMLElement>(`[data-notification-order-id="${orderId}"]`)
      : null;
    const element = orderElement ?? root.querySelector<HTMLElement>(`[data-notification-focus-anchor="${sessionId}"]`);
    if (!element) {
      return;
    }

    if (!orderElement && focusedNotificationKeyRef.current === `${focusKey}:fallback`) {
      return;
    }
    focusedNotificationKeyRef.current = orderElement ? focusKey : `${focusKey}:fallback`;

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.focus({ preventScroll: true });
  }, [expandedFoodSessionId, foodOrdersBySession, notificationFocus, recentSessions, settlementsOpen, sortedLiveUnits]);

  useEffect(() => {
    if (!notificationFocus) {
      return;
    }

    const timer = window.setTimeout(() => setNotificationFocus(null), 8000);
    return () => window.clearTimeout(timer);
  }, [notificationFocus]);

  // Open the settlements drawer when a notification targets an ended session,
  // otherwise its focus anchor stays hidden inside the collapsed details.
  useEffect(() => {
    if (notificationFocus && recentSessions.some((session) => session.id === notificationFocus.sessionId)) {
      setSettlementsOpen(true);
    }
  }, [notificationFocus, recentSessions]);

  const getStopForm = (sessionId: string) => stopForms[sessionId] ?? { note: "", payment_collected: false, payment_method: "cash" as PaymentMethod, payment_note: "" };

  const updateStopForm = <Field extends "note" | "payment_collected" | "payment_method" | "payment_note">(
    sessionId: string,
    field: Field,
    value: string | boolean,
  ) => {
    setStopForms((current) => ({
      ...current,
      [sessionId]: { ...getStopForm(sessionId), [field]: value },
    }));
  };

  const handleStartWalkIn = async (payload: StartWalkInSessionInput) => {
    const started = await onStartWalkInSession(payload);
    if (started !== false) setWalkInUnitId(null);
  };

  const handleUpdateFoodOrderStatus = async (sessionId: string, orderId: string, nextStatus: ProviderFoodOrderStatus) => {
    const venueId = selectedVenue?.id;
    const venueGeneration = foodOrderVenueGenerationRef.current;
    if (!venueId) return;
    setUpdatingFoodOrderId(orderId);
    setFoodOrderError(null);
    try {
      const updatedOrder = await providerApi.updateFoodOrderStatus(orderId, nextStatus);
      if (
        venueGeneration !== foodOrderVenueGenerationRef.current ||
        foodVenueIdRef.current !== venueId ||
        updatedOrder.venue_id !== venueId
      ) return;
      setFoodOrdersBySession((current) => ({
        ...current,
        [sessionId]: (current[sessionId] ?? []).map((order) => order.id === orderId ? updatedOrder : order),
      }));
      setFoodOrderNotice(t("Order marked {status}.", { status: t(formatFoodOrderStatus(nextStatus)).toLowerCase() }));
      void loadFoodOrders(venueId);
    } catch (requestError) {
      if (venueGeneration === foodOrderVenueGenerationRef.current && foodVenueIdRef.current === venueId) setFoodOrderError(requestError instanceof ApiError ? requestError.detail : t("Could not update food order status."));
    } finally {
      if (venueGeneration === foodOrderVenueGenerationRef.current && foodVenueIdRef.current === venueId) setUpdatingFoodOrderId(null);
    }
  };

  const hasActiveUnitFilters =
    unitStatusFilter !== "all" || unitTypeFilter !== "all" || unitSearch.trim() !== "";
  const clearUnitFilters = () => {
    setUnitStatusFilter("all");
    setUnitTypeFilter("all");
    setUnitSearch("");
  };

  const renderFoodPanel = (session: ProviderSession) => {
    const orders = [...(foodOrdersBySession[session.id] ?? [])].sort((left, right) => {
      return right.created_at.localeCompare(left.created_at) || left.id.localeCompare(right.id);
    });
    return (
      <div className="grid gap-2">
        <div className="provider-food-panel-head">
          <p className="ops-label">{t("Food orders")}{orders.some((order) => order.status === "pending") ? <span className="ml-2 text-amber-200">· {t("Needs attention")}</span> : null}</p>
          <span aria-live="polite" className="provider-food-sync text-xs text-slate-400">{foodOrdersLoading ? t("Syncing menu") : ""}</span>
        </div>

        {orders.length === 0 ? (
          <div className="rounded-[10px] border border-dashed border-white/10 px-3 py-2 text-xs text-slate-400">{t("No food orders yet for this session.")}</div>
        ) : (
          <div className="grid gap-2">
            {orders.map((order) => (
              <div
                className={`ops-record ops-record-dense grid gap-2 outline-none focus:ring-2 focus:ring-amber-300/70${notificationFocus?.orderId === order.id ? " ring-2 ring-amber-300/80" : ""}${order.status === "delivered" || order.status === "cancelled" ? " opacity-55" : ""}`}
                data-notification-order-id={order.id}
                key={order.id}
                tabIndex={-1}
              >
                <div className="provider-food-order-heading">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{t("Ordered {time}", { time: formatClock(order.created_at, venueTimezone, locale) })}</p>
                    {order.placed_by_customer ? <p className="text-xs text-slate-400">{t("From the app")}</p> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="ops-chip" data-tone={getFoodOrderTone(order.status)}>{order.status === "pending" ? t("New") : t(formatFoodOrderStatus(order.status))}</span>
                    <span className="ops-chip">{formatPricingAmount(order.total_amount)}</span>
                  </div>
                </div>
                <div className="grid gap-1 text-xs text-slate-300">
                  {order.items.map((item) => (
                    <p key={item.id}>{t("{quantity} x {name} · {price} each", { name: item.item_name, price: formatPricingAmount(item.unit_price), quantity: item.quantity })}</p>
                  ))}
                </div>
                {order.notes ? <p className="text-xs text-slate-400">{order.notes}</p> : null}
                  <div className="provider-food-order-actions flex flex-wrap gap-2">
                    {getFoodOrderActions(order.status).map((action) => (
                      <button
                        className="ops-button"
                        data-size="sm"
                        data-variant={action.nextStatus === "cancelled" ? "danger" : "secondary"}
                        aria-busy={updatingFoodOrderId === order.id}
                        disabled={updatingFoodOrderId !== null}
                        key={action.nextStatus === "cancelled" ? "cancel" : "advance"}
                        onClick={() => void handleUpdateFoodOrderStatus(session.id, order.id, action.nextStatus)}
                        type="button"
                      >
                        {t(action.label)}
                      </button>
                    ))}
                  </div>
              </div>
            ))}
          </div>
        )}

        {session.status !== "active" ? null : (
          <div className="provider-food-composer">
            <p className="ops-label">{t("Add an order")}</p>
            <OrderComposer
              initialSessionId={session.id}
              menu={foodMenuItems}
              menuError={null}
              onPlaced={(order) => {
                if (foodVenueIdRef.current !== order.venue_id) return;
                setFoodOrdersBySession((current) => mergeFoodOrder(current, order));
                setFoodOrderNotice(t("Order sent to the queue."));
                void loadFoodOrders(order.venue_id);
                void reloadFoodMenu(order.venue_id);
              }}
              sessions={[session]}
            />
          </div>
        )}
      </div>
    );
  };

  const renderUnitCell = (record: LiveUnitRecord) => {
    const { session, status, unit } = record;

    if (!session) {
      return (
        <div
          className="floor-unit"
          data-status={status}
          key={unit.id}
        >
          <div className="floor-unit-head">
            <span className="ops-status-dot shrink-0" />
            <span className="floor-unit-name">{unit.name}</span>
            {unit.is_vip ? <span className="ops-chip" data-tone="warning">{t("VIP")}</span> : null}
          </div>
          <p className="floor-unit-meta">
            {t(getUnitTypeLabel(unit.type))} · {t(unit.capacity === 1 ? "{count} seat" : "{count} seats", { count: unit.capacity })}
          </p>
          {status === "available" ? (
            <div className="floor-unit-foot">
              <p className="floor-unit-meta">{t("{amount} an hour", { amount: formatEgp(unit.base_hourly_rate, resolvedLanguage) })}</p>
              <button className="ops-button w-full" data-variant="primary" disabled={creatingWalkIn} onClick={() => setWalkInUnitId(unit.id)} type="button">
                {t("Start walk-in")}
              </button>
            </div>
          ) : (
            <div className="floor-unit-foot">
              <span className="ops-chip justify-self-start" data-tone={getUnitStatusTone(status)}>{t(getUnitStatusLabel(status))}</span>
              <p className="floor-unit-meta">
                {status === "reserved"
                  ? t("Held for a booking.")
                  : status === "blocked"
                    ? t("Blocked. Change it in Venue setup → Units.")
                    : t("Offline. Change it in Venue setup → Units.")}
              </p>
            </div>
          )}
        </div>
      );
    }

    const elapsedMinutes = getElapsedMinutes(session, nowMs);
    const overtimeMinutes = getOvertimeMinutes(session, nowMs);
    const remainingSeconds = getRemainingSeconds(session, nowMs);
    const progress = getSessionProgress(session, nowMs);
    const escalation = getSessionEscalation(session, nowMs);
    const elapsedTotalAmount = calculateSessionElapsedTotalAmount(session, unit, venueTimezone, elapsedMinutes);
    const plannedTotalAmount = calculateSessionPlannedTotalAmount(session, unit, venueTimezone);
    const stopForm = getStopForm(session.id);
    const isFoodOpen = expandedFoodSessionId === session.id;
    const isEnding = endingSessionId === session.id;
    const sessionOrders = foodOrdersBySession[session.id] ?? [];
    const openOrderCount = sessionOrders.filter((order) => isActionableFoodOrderStatus(order.status)).length;
    const highlightSessionCard = notificationFocus?.sessionId === session.id &&
      !(notificationFocus.orderId && sessionOrders.some((order) => order.id === notificationFocus.orderId));
    const timerLabel = remainingSeconds === null ? "Playing for" : remainingSeconds >= 0 ? "Time left" : "Over time by";
    const timerValue = remainingSeconds === null
      ? formatDuration(elapsedMinutes, t)
      : remainingSeconds >= 0
        ? formatCountdown(remainingSeconds)
        : formatDuration(Math.ceil(Math.abs(remainingSeconds) / 60), t);
    const busy = extendingSessionId === session.id || stoppingSessionId === session.id;

    return (
      <div
        className={`floor-unit outline-none focus:ring-2 focus:ring-amber-300/70${highlightSessionCard ? " ring-2 ring-amber-300/80" : ""}`}
        data-escalation={escalation}
        data-notification-focus-anchor={session.id}
        data-notification-session-id={session.id}
        data-status={status}
        key={unit.id}
        tabIndex={-1}
      >
        <div className="floor-unit-head">
          <span className="ops-status-dot shrink-0" />
          <span className="floor-unit-name">{unit.name}</span>
          {unit.is_vip ? <span className="ops-chip" data-tone="warning">{t("VIP")}</span> : null}
          {session.source === "booking" ? <span className="ops-chip">{t("Booking")}</span> : null}
        </div>
        <p className="floor-unit-guest">{session.customer_name}</p>

        <div className="floor-unit-timer">
          <div className="min-w-0">
            <p className="floor-unit-timer-label">{escalation === "overtime" ? <Hourglass aria-hidden="true" size={12} /> : null}{t(timerLabel)}</p>
            <p className="floor-unit-timer-value">{timerValue}</p>
          </div>
          <div className="text-end">
            <p className="floor-unit-timer-label">{t("Time so far")}</p>
            <p className="floor-unit-amount">{formatEgp(elapsedTotalAmount, resolvedLanguage)}</p>
          </div>
        </div>
        {progress !== null ? (
          <div aria-hidden="true" className="floor-unit-progress"><div style={{ width: `${progress}%` }} /></div>
        ) : null}

        <div className="floor-unit-add" role="group" aria-label={t("Add time")}>
          <span className="floor-unit-timer-label">{t("Add time")}</span>
          {[15, 30, 60].map((minutes) => (
            <button
              aria-label={t("Add {minutes} minutes", { minutes })}
              className="ops-button"
              data-size="sm"
              data-variant="secondary"
              disabled={busy}
              key={minutes}
              onClick={() => void onExtendSession(session.id, minutes)}
              type="button"
            >
              <bdi dir="ltr">{extendingSessionId === session.id ? "…" : `+${minutes}`}</bdi>
            </button>
          ))}
        </div>

        <div className="floor-unit-actions">
          <button className="ops-button" data-variant={isFoodOpen ? "primary" : "secondary"} onClick={() => setExpandedFoodSessionId(isFoodOpen ? null : session.id)} type="button">
            <ChefHat aria-hidden="true" size={15} /> {t("Food")}
            {openOrderCount > 0 ? <span className="provider-nav-badge" data-tone="attention">{openOrderCount}</span> : null}
          </button>
          <button
            className="ops-button"
            data-variant="danger"
            disabled={busy}
            onClick={() => setEndingSessionId(session.id)}
            type="button"
          >
            {t("End")}
          </button>
        </div>

        <Dialog onOpenChange={(open) => { if (!open) setEndingSessionId(null); }} open={isEnding}>
          <DialogContent aria-describedby={undefined} className="max-h-[85vh] max-w-md overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("End session")}</DialogTitle>
              <DialogDescription>{unit.name} · {session.customer_name}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-400">
              <span>{t("Started {value}", { value: formatClock(session.started_at, venueTimezone, locale) })}</span>
              <span aria-hidden="true" className="text-slate-600">·</span>
              <span>{session.planned_end_at ? t("Ends {value}", { value: formatClock(session.planned_end_at, venueTimezone, locale) }) : t("Open ended")}</span>
              <span aria-hidden="true" className="text-slate-600">·</span>
              <span>{t("Elapsed {value}", { value: formatDuration(elapsedMinutes, t) })}</span>
              {plannedTotalAmount !== null ? (
                <>
                  <span aria-hidden="true" className="text-slate-600">·</span>
                  <span>{t("Planned {value}", { value: formatPricingAmount(plannedTotalAmount) })}</span>
                </>
              ) : null}
              {overtimeMinutes > 0 ? (
                <>
                  <span aria-hidden="true" className="text-slate-600">·</span>
                  <span className="font-semibold text-red-100">{t("{value} overtime", { value: formatDuration(overtimeMinutes, t) })}</span>
                </>
              ) : null}
            </div>

            <div className="grid gap-2">
              <label className="ops-field">
                <span className="ops-label">{t("End note")}</span>
                <input className="ops-input" onChange={(event) => updateStopForm(session.id, "note", event.target.value)} placeholder={t("Reason for stopping or handoff note.")} value={stopForm.note} />
              </label>
              <label className="ops-checkbox-row">
                <input checked={stopForm.payment_collected} className="size-4 rounded border-white/20 bg-slate-900 text-emerald-300" onChange={(event) => updateStopForm(session.id, "payment_collected", event.target.checked)} type="checkbox" />
                {t("Payment was already collected at the desk")}
              </label>
              {stopForm.payment_collected ? (
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="ops-field">
                    <span className="ops-label">{t("Payment method")}</span>
                    <select className="ops-select" onChange={(event) => updateStopForm(session.id, "payment_method", event.target.value as PaymentMethod)} value={stopForm.payment_method}>
                      <option value="cash">{t("Cash")}</option>
                      <option value="card">{t("Card")}</option>
                      <option value="transfer">{t("Transfer")}</option>
                      <option value="other">{t("Other")}</option>
                    </select>
                  </label>
                  {stopForm.payment_method === "cash" ? (
                    <div className="sm:col-span-full"><NoShiftCashNotice isOnline={isOnline} venue={selectedVenue} /></div>
                  ) : null}
                  <label className="ops-field">
                    <span className="ops-label">{t("Payment note")}</span>
                    <input className="ops-input" onChange={(event) => updateStopForm(session.id, "payment_note", event.target.value)} placeholder={t("Desk payment, card approved, split bill...")} value={stopForm.payment_note} />
                  </label>
                </div>
              ) : null}
            </div>

            <DialogFooter>
              <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setEndingSessionId(null)} type="button">
                {t("Cancel")}
              </button>
              <button
                className="ops-button"
                data-size="sm"
                data-variant="danger"
                disabled={stoppingSessionId === session.id}
                onClick={() => void onStopSession(session.id, {
                  note: stopForm.note || null,
                  payment_collected: stopForm.payment_collected,
                  payment_method: stopForm.payment_collected ? stopForm.payment_method : null,
                  payment_note: stopForm.payment_collected ? stopForm.payment_note || null : null,
                })}
                type="button"
              >
                {stoppingSessionId === session.id ? t("Stopping...") : t("End session")}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    );
  };

  const occupiedCount = unitStatusCounts.occupied;
  const overtimeCount = liveUnits.filter((record) => record.session && getSessionEscalation(record.session, nowMs) === "overtime").length;
  // Filters earn their space only on bigger floors.
  const showFilters = liveUnits.length > FILTERS_FROM_UNITS;

  return (
    <div className="ops-surface ops-surface-dense grid gap-3" ref={rootRef}>
      <header className="ops-page-header">
        <div className="min-w-0">
          <h1 className="ops-page-title">{t("Floor")}</h1>
          <p className="ops-copy text-sm">
            {selectedVenue
              ? [
                t("{count} playing", { count: occupiedCount }),
                t("{count} free", { count: unitStatusCounts.available }),
                unitStatusCounts.reserved ? t("{count} reserved", { count: unitStatusCounts.reserved }) : null,
                overtimeCount ? t("{count} over time", { count: overtimeCount }) : null,
              ].filter(Boolean).join(" · ")
              : t("No venue selected")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isLoading ? <span className="ops-chip">{t("Loading")}</span> : null}
          <span className="ops-chip" data-tone="info">{formatLocalTime(nowMs, venueTimezone, locale)}</span>
        </div>
      </header>

      {notice ? <div className="ops-banner" data-tone="success">{notice}</div> : null}
      {error ? <div className="ops-banner" data-tone="danger">{error}</div> : null}
      {foodOrderError && !foodModalSession ? <div className="ops-banner" data-tone="danger">{foodOrderError}</div> : null}

      {selectedVenue && actionableFoodOrders.length > 0 ? (
        <button className="floor-orders-link" onClick={() => navigateToSection?.("orders")} type="button">
          <ChefHat aria-hidden="true" size={16} />
          <span className="min-w-0 flex-1">{t(actionableFoodOrders.length === 1 ? "{count} food order open" : "{count} food orders open", { count: actionableFoodOrders.length })}</span>
          <span className="floor-orders-link-cta">{t("Open orders")} →</span>
        </button>
      ) : null}

      {!selectedVenue ? (
        <div className="ops-empty px-4 py-10">{t("Select a venue to run live sessions.")}</div>
      ) : (
        <>
          {showFilters ? (
            <section className="flex flex-wrap items-center gap-1.5">
              {(["all", ...UNIT_STATUS_ORDER] as Array<UnitStatus | "all">)
                .filter((statusOption) => statusOption === "all" || unitStatusCounts[statusOption] > 0)
                .map((statusOption) => (
                  <button
                    aria-pressed={unitStatusFilter === statusOption}
                    className="ops-filter-pill"
                    data-active={unitStatusFilter === statusOption}
                    key={statusOption}
                    onClick={() => setUnitStatusFilter(statusOption)}
                    type="button"
                  >
                    {t(getUnitStatusLabel(statusOption))} ({statusOption === "all" ? liveUnits.length : unitStatusCounts[statusOption]})
                  </button>
                ))}
              {visibleUnitTypes.length > 1 ? visibleUnitTypes.map((type) => (
                <button className="ops-filter-pill" data-active={unitTypeFilter === type} key={type} onClick={() => setUnitTypeFilter(unitTypeFilter === type ? "all" : type)} type="button">
                  {t(getUnitTypeLabel(type))}
                </button>
              )) : null}
              <label className="ops-field w-full min-[420px]:ml-auto min-[420px]:w-56">
                <span className="sr-only">{t("Search units")}</span>
                <input className="ops-input" onChange={(event) => setUnitSearch(event.target.value)} placeholder={t("Search units or guests...")} value={unitSearch} />
              </label>
              {hasActiveUnitFilters ? (
                <button className="ops-button" data-size="sm" data-variant="secondary" onClick={clearUnitFilters} type="button">
                  {t("Clear filters")} ({t("{visible} of {total} units", { total: liveUnits.length, visible: filteredLiveUnits.length })})
                </button>
              ) : null}
            </section>
          ) : null}

          {sortedLiveUnits.length === 0 ? (
            <div className="ops-empty px-4 py-10">{liveUnits.length === 0 ? t("No units yet. Add them in Venue setup → Units.") : t("No units match the current filters.")}</div>
          ) : (
            <section className="floor-grid">
              {sortedLiveUnits.map((record) => renderUnitCell(record))}
            </section>
          )}
        </>
      )}

      <WalkInDialog
        busy={creatingWalkIn}
        onClose={() => setWalkInUnitId(null)}
        onStart={handleStartWalkIn}
        timezone={venueTimezone}
        unit={availableUnits.find((unit) => unit.id === walkInUnitId) ?? null}
        venueId={selectedVenue?.id ?? ""}
      />

      <details
        className="ops-subtle-surface grid gap-3 p-3"
        onToggle={(event) => setSettlementsOpen(event.currentTarget.open)}
        open={settlementsOpen}
      >
        <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-3">
          <div>
            <p className="home-section-title">{t("Ended recently")}</p>
            <p className="mt-0.5 text-xs leading-5 text-slate-400">{t("Sessions that just finished. Take payment for any that aren't paid yet.")}</p>
          </div>
          {unpaidRecentCount > 0 ? (
            <div className="ops-chip" data-tone="warning">{t(unpaidRecentCount === 1 ? "{count} not paid yet" : "{count} not paid yet", { count: unpaidRecentCount })}</div>
          ) : (
            <div className="ops-chip">{t(recentSessions.length === 1 ? "{count} recent session" : "{count} recent sessions", { count: recentSessions.length })}</div>
          )}
        </summary>

        {recentSessions.length === 0 ? (
          <div className="ops-empty px-4 py-8">{t("No completed sessions in the current venue yet.")}</div>
        ) : (
          <div className="grid gap-2">
            {[...recentSessions].sort((a, b) => Number(a.payment_collected) - Number(b.payment_collected)).map((session) => {
              const highlightRecentSession = notificationFocus?.sessionId === session.id &&
                !(notificationFocus.orderId && actionableFoodOrderIds.has(notificationFocus.orderId));
              return (
              <div
                className={`ops-record ops-record-dense flex flex-wrap items-center justify-between gap-2 outline-none focus:ring-2 focus:ring-amber-300/70${highlightRecentSession ? " ring-2 ring-amber-300/80" : ""}`}
                data-notification-focus-anchor={session.id}
                data-notification-session-id={session.id}
                key={session.id}
                tabIndex={-1}
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{session.customer_name}</p>
                    <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{session.unit_name} · {session.source === "booking" ? t("booking") : t("walk in")}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-400">{t("Total {value}", { value: formatPricingAmount(session.elapsed_total_amount) })}</span>
                  <span className="ops-chip" data-tone={session.payment_collected ? "success" : "warning"}>{session.payment_collected ? t("Paid") : t("Not paid yet")}</span>
                  {session.payment_method ? <span className="ops-chip">{t(session.payment_method)}</span> : null}
                  {onCheckoutSession ? (
                    <button className="ops-button" data-size="sm" data-variant="primary" onClick={() => onCheckoutSession(session.id)} type="button">
                      {session.payment_collected ? t("Receipt") : t("Take payment")}
                    </button>
                  ) : null}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </details>

      {foodModalSession ? createPortal(
        <div
          aria-labelledby={foodModalTitleId}
          aria-modal="true"
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setExpandedFoodSessionId(null);
            }
          }}
          role="dialog"
        >
          <div
            className="ops-surface provider-food-dialog w-full max-w-lg gap-3 p-4 outline-none"
            ref={foodModalRef}
            tabIndex={-1}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="ops-title" id={foodModalTitleId}>{t("Food & drinks")}</p>
                <p className="ops-muted truncate">{foodModalSession.unit_name} · {foodModalSession.customer_name}</p>
              </div>
              <button className="ops-button shrink-0" data-size="sm" data-variant="secondary" onClick={() => setExpandedFoodSessionId(null)} type="button">
                {t("Close")}
              </button>
            </div>
            <div className="provider-food-dialog-body">
              {renderFoodPanel(foodModalSession)}
              <p aria-live="polite" role={foodOrderError ? "alert" : "status"} className="provider-food-feedback">{foodOrderError ?? foodOrderNotice ?? ""}</p>
            </div>
          </div>
        </div>, document.body
      ) : null}
    </div>
  );
}
